import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Chip,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';

interface ImageItem {
  id: string;
  name: string;
  url: string;
  folderId: string;
  createdAt: string;
  createdBy: string;
  size?: number;
  type?: string;
}

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  images: ImageItem[];
  currentImageIndex: number;
  onImageChange: (index: number) => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  images,
  currentImageIndex,
  onImageChange
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  
  const [zoom, setZoom] = useState(1);
  const [imageLoading, setImageLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentImage = images[currentImageIndex];

  // Zoom reset when image changes
  useEffect(() => {
    setZoom(1);
    setDragPosition({ x: 0, y: 0 });
  }, [currentImageIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!open) return;

      switch (event.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        case '0':
          handleZoomReset();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [open, currentImageIndex]);

  const handlePrevious = () => {
    if (currentImageIndex > 0) {
      onImageChange(currentImageIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentImageIndex < images.length - 1) {
      onImageChange(currentImageIndex + 1);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev * 1.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev / 1.2, 0.5));
  };

  const handleZoomReset = () => {
    setZoom(1);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageLoadStart = () => {
    setImageLoading(true);
  };

  const handleDownload = () => {
    if (currentImage?.url) {
      const link = document.createElement('a');
      link.href = currentImage.url;
      link.download = currentImage.name || `${tt('image', 'image')}-${currentImageIndex + 1}`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Mouse drag handlers
  const handleMouseDown = (event: React.MouseEvent) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: event.clientX - dragPosition.x,
        y: event.clientY - dragPosition.y
      });
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setDragPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Dosya boyutunu formatla
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!currentImage) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          borderRadius: isMobile ? 0 : 2,
          maxWidth: '95vw',
          maxHeight: '95vh',
          margin: isMobile ? 0 : 2
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          py: 1,
          px: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <PhotoLibraryIcon sx={{ mr: 1 }} />
          <Typography variant="subtitle1" noWrap sx={{ mr: 2 }}>
            {currentImage.name}
          </Typography>
          <Chip
            label={`${currentImageIndex + 1} / ${images.length}`}
            size="small"
            sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', color: 'white' }}
          />
        </Box>
        
        {/* Control buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => setShowInfo(!showInfo)}
            sx={{ color: 'white', mr: 1 }}
            title={tt('Bilgi', 'Info')}
          >
            <InfoIcon />
          </IconButton>
          <IconButton
            onClick={handleDownload}
            sx={{ color: 'white', mr: 1 }}
            title={tt('İndir', 'Download')}
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            title={tt('Kapat (Esc)', 'Close (Esc)')}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Main content */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '60vh'
        }}
      >
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              disabled={currentImageIndex === 0}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' }
              }}
              title={tt('Önceki (←)', 'Previous (←)')}
            >
              <NavigateBeforeIcon fontSize="large" />
            </IconButton>
            
            <IconButton
              onClick={handleNext}
              disabled={currentImageIndex === images.length - 1}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                '&:disabled': { color: 'rgba(255, 255, 255, 0.3)' }
              }}
              title={tt('Sonraki (→)', 'Next (→)')}
            >
              <NavigateNextIcon fontSize="large" />
            </IconButton>
          </>
        )}

        {/* Image container */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
            overflow: 'hidden'
          }}
          onClick={zoom === 1 ? handleZoomIn : undefined}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <Fade in={!imageLoading} timeout={300}>
            <Box
              component="img"
              src={currentImage.url}
              alt={currentImage.name}
              onLoad={handleImageLoad}
              onLoadStart={handleImageLoadStart}
              sx={{
                maxWidth: zoom === 1 ? '100%' : 'none',
                maxHeight: zoom === 1 ? '100%' : 'none',
                width: zoom > 1 ? `${zoom * 100}%` : 'auto',
                height: zoom > 1 ? `${zoom * 100}%` : 'auto',
                objectFit: 'contain',
                transform: `translate(${dragPosition.x}px, ${dragPosition.y}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            />
          </Fade>
        </Box>

        {/* Loading indicator */}
        {imageLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white'
            }}
          >
            <Typography>{tt('Yükleniyor...', 'Loading...')}</Typography>
          </Box>
        )}

        {/* Info panel */}
        {showInfo && (
          <Zoom in={showInfo}>
            <Paper
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                p: 2,
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                minWidth: 200,
                maxWidth: 300
              }}
            >
              <Typography variant="subtitle2" gutterBottom>
                {tt('Resim Bilgileri', 'Image Information')}
              </Typography>
              <Typography variant="caption" component="div">
                <strong>{tt('Ad:', 'Name:')}</strong> {currentImage.name}
              </Typography>
              <Typography variant="caption" component="div">
                <strong>{tt('Boyut:', 'Size:')}</strong> {currentImage.size ? formatFileSize(currentImage.size) : tt('Bilinmiyor', 'Unknown')}
              </Typography>
              <Typography variant="caption" component="div">
                <strong>{tt('Tür:', 'Type:')}</strong> {currentImage.type || tt('Bilinmiyor', 'Unknown')}
              </Typography>
              <Typography variant="caption" component="div">
                <strong>{tt('Tarih:', 'Date:')}</strong> {new Date(currentImage.createdAt).toLocaleDateString(localeCode)}
              </Typography>
            </Paper>
          </Zoom>
        )}
      </DialogContent>

      {/* Footer controls */}
      <DialogActions
        sx={{
          bgcolor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          px: 2,
          py: 1,
          justifyContent: 'space-between',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ mr: 2 }}>
            Zoom: {Math.round(zoom * 100)}%
          </Typography>
          <IconButton
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
            sx={{ color: 'white', mr: 1 }}
            size="small"
            title={tt('Uzaklaştır (-)', 'Zoom Out (-)')}
          >
            <ZoomOutIcon />
          </IconButton>
          <IconButton
            onClick={handleZoomReset}
            sx={{ color: 'white', mr: 1 }}
            size="small"
            title={tt('Sıfırla (0)', 'Reset (0)')}
          >
            <ZoomOutMapIcon />
          </IconButton>
          <IconButton
            onClick={handleZoomIn}
            disabled={zoom >= 3}
            sx={{ color: 'white' }}
            size="small"
            title={tt('Yakınlaştır (+)', 'Zoom In (+)')}
          >
            <ZoomInIcon />
          </IconButton>
        </Box>
        
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {tt('← → Gezinme | + - Zoom | Esc Kapat', '← → Navigate | + - Zoom | Esc Close')}
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default ImagePreviewModal; 